import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	ViewChild,
	effect,
	inject,
	input,
	signal,
	computed,
} from '@angular/core';
import { debounceTime, Subscription } from 'rxjs';

import { STATUS_FLAGS } from '../timing-tower/IRaceTiming';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { ThemeService } from '../shared/theme.service';

import { CarManagerService, CarState } from './car-manager.service';
import { TrackRendererService, TrackTurn } from './track-renderer.service';
import { CommonModule } from '@angular/common';

type TrackTransform = {
	scale: number;
	offsetX: number;
	offsetY: number;
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
};

@Component({
	selector: 'vb-lxp-map',
	templateUrl: './map.component.html',
	styleUrl: './map.component.css',
	imports: [CommonModule],
	providers: [],
})
export class VbLxpMapComponent implements AfterViewInit, OnDestroy {
	@ViewChild('trackCanvas', { static: true }) trackCanvas!: ElementRef<HTMLCanvasElement>;
	@ViewChild('carCanvas', { static: true }) carCanvas!: ElementRef<HTMLCanvasElement>;
	@ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;

	private carManager = inject(CarManagerService);
	private trackService = inject(TrackRendererService);
	private themeService = inject(ThemeService);
	private raceSessionService = inject(RaceSessionService);

	private trackCtx!: CanvasRenderingContext2D;
	private carCtx!: CanvasRenderingContext2D;

	private gpsSub: Subscription | null = null;
	private lastTimestamp: number | null = null;
	private rafId: number | null = null;

	private gpsIntervalHistory: number[] = [];
	private lastGpsTimestamp: number | null = null;
	private readonly GPS_HISTORY_SIZE = 20;

	private cachedTransform: TrackTransform | null = null;

	private resizeObserver: ResizeObserver;
	private canvasReady = signal(false);
	track = input('');
	showLabels = input(true);
	selectedCarIds = input([]);
	rotation = input(0);
	entries = computed(() => this.raceSessionService.raceTeamsPosition());
	width = signal(0);
	height = signal(0);

	carsInPit = computed(() => {
		const carNumbers = this.raceSessionService
			.raceTeamsPosition()
			.map((entry) => ({
				foreground: entry.classDetails.foreground || '#ffffff',
				background: entry.classDetails.background || '#000000',
				number: entry.number,
				isPit: entry.participantDetails.status === 'BOX',
			}))
			.sort((a, b) => a.number.localeCompare(b.number));
		const gpsCarNumbers = this.raceSessionService
			.gpsPosition()
			.map((entry) => Object.keys(entry)[0]);
		return carNumbers.filter((item) => !gpsCarNumbers.includes(item.number) || item.isPit);
	});

	constructor() {
		effect(async () => {
			if (!this.canvasReady() || !this.track()) return;
			await this.restartSimulation(this.track());
		});

		effect(() => {
			const _r = this.rotation();
			const _w = this.width();
			const _h = this.height();
			if (this.canvasReady() && this.trackService.isLoaded()) {
				setTimeout(() => {
					this.cachedTransform = this.computeTrackTransform();
					this.drawTrackLayer();
					this.cachedTransform = null;
				});
			}
		});
	}

	async ngAfterViewInit() {
		this.resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				this.width.set(entry.contentRect.width);
				this.height.set(entry.contentRect.height);
			}
		});
		this.resizeObserver.observe(this.mapContainer.nativeElement);
		this.trackCtx = this.trackCanvas.nativeElement.getContext('2d')!;
		this.carCtx = this.carCanvas.nativeElement.getContext('2d')!;

		document.addEventListener('visibilitychange', this.visibilityListener);

		this.startRenderLoop();
		this.canvasReady.set(true);
	}

	ngOnDestroy(): void {
		this.resizeObserver.disconnect();
		document.removeEventListener('visibilitychange', this.visibilityListener);
		this.gpsIntervalHistory = [];
		this.lastGpsTimestamp = null;
		this.carManager.reset();
		this.gpsSub?.unsubscribe();

		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}

		this.lastTimestamp = null;
	}

	private visibilityListener = () => {
		if (document.visibilityState === 'visible') {
			this.lastTimestamp = null;
			this.carManager.snapAllToGps();
			if (this.track()) {
				this.restartSimulation(this.track());
			}
		}
	};

	// -----------------------------------------------------------------------
	// GPS interval tracking
	// -----------------------------------------------------------------------

	private recordGpsBatch() {
		const now = Date.now();
		if (this.lastGpsTimestamp !== null) {
			const interval = now - this.lastGpsTimestamp;
			if (interval > 0 && interval < 2000) {
				this.gpsIntervalHistory.push(interval);
				if (this.gpsIntervalHistory.length > this.GPS_HISTORY_SIZE) {
					this.gpsIntervalHistory.shift();
				}
			}
		}
		this.lastGpsTimestamp = now;
	}

	private getAverageGpsInterval(): number {
		if (this.gpsIntervalHistory.length === 0) return 100;
		return this.gpsIntervalHistory.reduce((a, b) => a + b, 0) / this.gpsIntervalHistory.length;
	}

	// -----------------------------------------------------------------------
	// Simulation
	// -----------------------------------------------------------------------

	private async restartSimulation(trackName: string) {
		this.gpsSub?.unsubscribe();
		this.gpsSub = null;

		this.lastTimestamp = null;
		this.cachedTransform = null;
		this.carManager.reset();

		const trackPath = `/assets/tracks/${trackName}`;
		await this.trackService.loadTrack(trackPath);

		this.cachedTransform = this.computeTrackTransform();
		this.drawTrackLayer();
		this.cachedTransform = null;

		this.gpsSub = this.raceSessionService.gpsPosition$
			.pipe(debounceTime(10))
			.subscribe((update) => {
				this.recordGpsBatch();
				for (const car of update) {
					const carNumber = Object.keys(car)[0];
					if (carNumber === 'SC' || carNumber === 'MC') continue;

					const data = car[carNumber];
					const status = data.status ?? 0;

					if (status & STATUS_FLAGS.InPit) {
						this.carManager.markInPit(carNumber);
						continue;
					}
					if (status & STATUS_FLAGS.PositionUnknown) continue;
					if (status & STATUS_FLAGS.Retired) {
						this.carManager.removeCar(carNumber);
						continue;
					}
					if (status & STATUS_FLAGS.Late) continue;
					if (status & STATUS_FLAGS.NoGpsPosition) continue;

					if (
						data.lat === 0 ||
						data.lon === 0 ||
						data.lat == null ||
						data.lon == null ||
						isNaN(data.lat) ||
						isNaN(data.lon)
					)
						continue;

					const [x, y] = this.trackService.latLonToXY(data.lat, data.lon);
					const classDetails = this.raceSessionService.getClassDetails(carNumber);
					const isOutLap = !!(status & STATUS_FLAGS.OutLap);

					this.carManager.updateCarGps(
						carNumber,
						x,
						y,
						classDetails.background,
						classDetails.foreground,
						isOutLap,
					);
				}
			});
	}

	// -----------------------------------------------------------------------
	// Render loop — car canvas only
	// -----------------------------------------------------------------------

	private startRenderLoop() {
		if (this.rafId !== null) return;

		const loop = (ts: number) => {
			this.renderFrame(ts);
			this.rafId = requestAnimationFrame(loop);
		};

		this.rafId = requestAnimationFrame(loop);
	}

	private renderFrame(timestamp: number) {
		if (!this.lastTimestamp) this.lastTimestamp = timestamp;
		const deltaTime = Math.min(timestamp - this.lastTimestamp, 50);
		this.lastTimestamp = timestamp;

		this.cachedTransform = this.computeTrackTransform();

		const ctx = this.carCtx;
		ctx.clearRect(0, 0, this.carCanvas.nativeElement.width, this.carCanvas.nativeElement.height);

		this.carManager.updatePositions(deltaTime, this.getAverageGpsInterval());

		for (const car of this.carManager
			.getCars()
			.filter((c) => !this.selectedCarIds().some((sc: string) => sc === c.id))) {
			this.drawCar(car);
		}

		for (const car of this.carManager
			.getCars()
			.filter((c) => this.selectedCarIds().some((sc: string) => sc === c.id))) {
			this.drawCar(car);
			ctx.restore();
		}

		// Remove cars marked in pit after drawing — one frame grace
		this.carManager.cars.forEach((car, id) => {
			if (car.inPit) this.carManager.cars.delete(id);
		});

		this.cachedTransform = null;
	}

	// -----------------------------------------------------------------------
	// Transform helpers
	// -----------------------------------------------------------------------

	private getTrackTransform(): TrackTransform {
		return this.cachedTransform ?? this.computeTrackTransform();
	}

	private computeTrackTransform(): TrackTransform {
		const points = this.trackService.getPoints();
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity;
		for (let i = 0; i < points.length; i += 2) {
			const x = points[i],
				y = points[i + 1];
			if (x < minX) minX = x;
			if (y < minY) minY = y;
			if (x > maxX) maxX = x;
			if (y > maxY) maxY = y;
		}

		const width = maxX - minX;
		const height = maxY - minY;

		const angleRad = (this.rotation() * Math.PI) / 180;
		const cos = Math.abs(Math.cos(angleRad));
		const sin = Math.abs(Math.sin(angleRad));
		const rotatedWidth = width * cos + height * sin;
		const rotatedHeight = width * sin + height * cos;

		const paddingX = 20;
		const paddingY = 40;
		const scale = Math.min(
			(this.trackCanvas.nativeElement.width - 2 * paddingX) / rotatedWidth,
			(this.trackCanvas.nativeElement.height - 2 * paddingY) / rotatedHeight,
		);

		const offsetX = (this.trackCanvas.nativeElement.width - width * scale) / 2;
		const offsetY = (this.trackCanvas.nativeElement.height - height * scale) / 2;

		return { scale, offsetX, offsetY, minX, minY, maxX, maxY };
	}

	private applyTrackTransform(ctx: CanvasRenderingContext2D) {
		const { scale, offsetX, offsetY, minX, minY } = this.getTrackTransform();
		const cx = this.trackCanvas.nativeElement.width / 2;
		const cy = this.trackCanvas.nativeElement.height / 2;
		const angleRad = (this.rotation() * Math.PI) / 180;

		ctx.translate(cx, cy);
		ctx.rotate(angleRad);
		ctx.translate(-cx, -cy);
		ctx.translate(offsetX, offsetY);
		ctx.scale(scale, scale);
		ctx.translate(-minX, -minY);
	}

	private trackToScreen(x: number, y: number): [number, number] {
		const { scale, offsetX, offsetY, minX, minY } = this.getTrackTransform();
		const cx = this.trackCanvas.nativeElement.width / 2;
		const cy = this.trackCanvas.nativeElement.height / 2;
		const angleRad = (this.rotation() * Math.PI) / 180;

		const tx = (x - minX) * scale + offsetX;
		const ty = (y - minY) * scale + offsetY;

		const cos = Math.cos(angleRad);
		const sin = Math.sin(angleRad);
		const screenX = cos * (tx - cx) - sin * (ty - cy) + cx;
		const screenY = sin * (tx - cx) + cos * (ty - cy) + cy;

		return [screenX, screenY];
	}

	// -----------------------------------------------------------------------
	// Track layer — drawn once onto trackCtx
	// -----------------------------------------------------------------------

	private drawTrackLayer() {
		if (!this.trackService.isLoaded()) return;

		const ctx = this.trackCtx;
		const w = this.trackCanvas.nativeElement.width;
		const h = this.trackCanvas.nativeElement.height;
		const { scale } = this.getTrackTransform();
		const sectors = this.trackService.getSectors();
		const pitLane = this.trackService.getPitLane();
		const startFinish = this.trackService.getStartFinish();
		const turns = this.trackService.getTurns();
		const isDark = this.themeService.isDarkMode();
		const fallbackColor = isDark ? 'white' : 'black';

		ctx.clearRect(0, 0, w, h);

		// --- Sectors ---
		for (const sector of sectors) {
			const pts = sector.points;
			if (pts.length < 4) continue;

			ctx.save();
			this.applyTrackTransform(ctx);
			ctx.strokeStyle = sector.color || fallbackColor;
			ctx.lineWidth = 4 / scale;
			ctx.lineJoin = 'round';
			ctx.lineCap = 'round';
			ctx.beginPath();
			ctx.moveTo(pts[0], pts[1]);
			for (let i = 2; i < pts.length; i += 2) {
				ctx.lineTo(pts[i], pts[i + 1]);
			}
			ctx.stroke();
			ctx.restore();
		}

		// --- Pit lane ---
		if (pitLane && pitLane.length >= 4) {
			ctx.save();
			this.applyTrackTransform(ctx);
			ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)';
			ctx.lineWidth = 2 / scale;
			ctx.setLineDash([8 / scale, 4 / scale]);
			ctx.lineJoin = 'round';
			ctx.lineCap = 'round';
			ctx.beginPath();
			ctx.moveTo(pitLane[0], pitLane[1]);
			for (let i = 2; i < pitLane.length; i += 2) {
				ctx.lineTo(pitLane[i], pitLane[i + 1]);
			}
			ctx.stroke();
			ctx.setLineDash([]);
			ctx.restore();
		}

		if (startFinish && startFinish.length > 0) {
			ctx.save();
			this.applyTrackTransform(ctx);
			ctx.strokeStyle = isDark ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)';
			ctx.lineWidth = 8 / scale;
			ctx.setLineDash([8 / scale, 4 / scale]);
			ctx.lineJoin = 'round';
			ctx.lineCap = 'round';
			ctx.beginPath();
			ctx.moveTo(startFinish[0], startFinish[1]);
			for (let i = 2; i < startFinish.length; i += 2) {
				ctx.lineTo(startFinish[i], startFinish[i + 1]);
			}
			ctx.stroke();
			ctx.setLineDash([]);
			ctx.restore();
		}

		// --- Turn labels (screen space — stay upright regardless of rotation) ---
		if (turns.length > 0 && this.showLabels()) {
			this.drawTurnLabels(ctx, turns, isDark);
		}
	}

	private drawTurnLabels(ctx: CanvasRenderingContext2D, turns: TrackTurn[], isDark: boolean) {
		for (const turn of turns) {
			const [screenX, screenY] = this.trackToScreen(turn.x, turn.y);

			const fontSize = 14;
			ctx.font = `bold ${fontSize}px sans-serif`;
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';

			const metrics = ctx.measureText(turn.label);
			const padX = 3;
			const padY = 2;
			const boxW = metrics.width + padX * 2;
			const boxH = fontSize + padY * 2;
			const boxX = screenX - boxW / 2;
			const boxY = screenY - boxH / 2;

			ctx.fillStyle = isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.55)';
			ctx.fillRect(boxX, boxY, boxW, boxH);

			ctx.fillStyle = isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)';
			ctx.fillText(turn.label, screenX, screenY);
		}
	}

	// -----------------------------------------------------------------------
	// Car layer — drawn every frame onto carCtx
	// -----------------------------------------------------------------------

	private drawCar(car: CarState) {
		const [screenX, screenY] = this.trackToScreen(car.x, car.y);
		const ctx = this.carCtx;
		const isSelected = this.selectedCarIds().some((sc: string) => sc === car.id);

		const fontSize = 18;
		const padX = 5;
		const padY = 3;
		const radius = 3;

		ctx.save();
		ctx.font = `bold ${fontSize}px sans-serif`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';

		const metrics = ctx.measureText(car.id);
		const boxW = metrics.width + padX * 2;
		const boxH = fontSize + padY * 2;
		const boxX = screenX - boxW / 2;
		const boxY = screenY - boxH / 2;

		const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
			ctx.beginPath();
			ctx.moveTo(x + r, y);
			ctx.lineTo(x + w - r, y);
			ctx.arcTo(x + w, y, x + w, y + r, r);
			ctx.lineTo(x + w, y + h - r);
			ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
			ctx.lineTo(x + r, y + h);
			ctx.arcTo(x, y + h, x, y + h - r, r);
			ctx.lineTo(x, y + r);
			ctx.arcTo(x, y, x + r, y, r);
			ctx.closePath();
		};

		if (isSelected) {
			// Solid fill with foreground border
			ctx.fillStyle = car.background || '#000000';
			roundRect(boxX, boxY, boxW, boxH, radius);
			ctx.fill();

			ctx.strokeStyle = car.foreground || '#ffffff';
			ctx.lineWidth = 1.5;
			roundRect(boxX, boxY, boxW, boxH, radius);
			ctx.stroke();

			ctx.fillStyle = car.foreground || '#ffffff';
			ctx.fillText(car.id, screenX, screenY);
		} else {
			const fontSize = 8;
			const dotRadius = 10;

			// Circle
			ctx.beginPath();
			ctx.arc(screenX, screenY, dotRadius, 0, Math.PI * 2);
			ctx.fillStyle = car.background || '#000000';
			ctx.fill();
			ctx.strokeStyle = car.foreground || '#ffffff';
			ctx.lineWidth = 1;
			ctx.stroke();

			// Number inside
			ctx.font = `bold ${fontSize}px sans-serif`;
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.fillStyle = car.foreground || '#ffffff';
			ctx.fillText(car.id, screenX, screenY);
		}

		ctx.restore();
	}
}
